// PozicovnaKalendar — kalendár požičovne (Mirko 18. 9. 2026: „skôr podobný rozpis v požičovni, aby on videl
// v kalendári, kde čo je"). Riadok = stroj, stĺpec = deň. Pruh = kto ho má a na ktorej zákazke.
// Po termíne (plánovaný návrat je za nami a vec nie je vrátená) je pruh červený, vrátené sivé.
// Voľné stroje majú prázdny riadok — na jeden pohľad vidno, čo je k dispozícii.
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'

const DEN_MS = 86400000
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
const dnesIso = () => iso(new Date())
const pondelok = (d) => { const x = new Date(d); const w = (x.getDay() + 6) % 7; x.setDate(x.getDate() - w); x.setHours(0, 0, 0, 0); return x }
const plusDni = (dstr, n) => iso(new Date(new Date(dstr + 'T00:00:00').getTime() + n * DEN_MS))
const rozdiel = (a, b) => Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / DEN_MS)
const DNI_SK = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']
const MES_SK = ['január', 'február', 'marec', 'apríl', 'máj', 'jún', 'júl', 'august', 'september', 'október', 'november', 'december']

const ROZSAHY = [{ k: 14, t: '2 týždne' }, { k: 35, t: '5 týždňov' }, { k: 70, t: '10 týždňov' }]

export default function PozicovnaKalendar({ polozky = [], onOtvorPolozku }) {
  const toast = useToast()
  const [dni, setDni] = useState(35)
  const [zaciatok, setZaciatok] = useState(() => iso(pondelok(new Date(Date.now() - 7 * DEN_MS))))
  const [vypozicky, setVypozicky] = useState([])
  const [nacitava, setNacitava] = useState(true)
  const [lenObsadene, setLenObsadene] = useState(true)
  const [hladat, setHladat] = useState('')

  const koniec = useMemo(() => plusDni(zaciatok, dni - 1), [zaciatok, dni])
  const dnes = dnesIso()

  const nacitaj = useCallback(() => {
    setNacitava(true)
    fetchAuth('/api/pozicovna/report?stav=vsetko&okno_od=' + zaciatok + '&okno_do=' + koniec)
      .then(r => r.ok ? r.json() : null)
      .then(d => setVypozicky((d && d.riadky) || []))
      .catch(() => toast.error('Kalendár sa nenačítal'))
      .finally(() => setNacitava(false))
  }, [zaciatok, koniec])
  useEffect(() => { nacitaj() }, [nacitaj])

  const stlpce = useMemo(() => {
    const out = []
    for (let i = 0; i < dni; i++) {
      const d = plusDni(zaciatok, i)
      const dt = new Date(d + 'T00:00:00')
      const w = (dt.getDay() + 6) % 7
      out.push({ d, den: DNI_SK[w], cislo: dt.getDate(), vikend: w >= 5, mesiac: dt.getMonth(), dnes: d === dnes, prvyVMes: dt.getDate() === 1 || i === 0 })
    }
    return out
  }, [zaciatok, dni, dnes])

  // riadky = stroje; ku každému jeho výpožičky v okne
  const riadky = useMemo(() => {
    const podla = {}
    for (const v of vypozicky) { (podla[v.item_id] = podla[v.item_id] || []).push(v) }
    const zoznam = (polozky || []).filter(p => !(p.parent_id || '').trim())
    const q = hladat.trim().toLowerCase()
    return zoznam
      .map(p => ({ polozka: p, vyp: podla[p.id] || [] }))
      .filter(r => (lenObsadene ? r.vyp.length > 0 : true))
      .filter(r => !q || hladaj([r.polozka.name, r.polozka.code, r.polozka.serial_number, ...r.vyp.map(v => v.osoba), ...r.vyp.map(v => v.project_id)]
        .filter(Boolean).join(' '), q))
      .sort((a, b) => (b.vyp.length > 0) - (a.vyp.length > 0) || String(a.polozka.name || '').localeCompare(String(b.polozka.name || ''), 'sk'))
  }, [vypozicky, polozky, lenObsadene, hladat])

  const pruh = (v) => {
    const od = String(v.start_date || '').slice(0, 10) || zaciatok
    const planovany = String(v.end_date || '').slice(0, 10)
    const vratene = String(v.return_date || '').slice(0, 10)
    const doDna = vratene || (planovany && planovany > dnes ? planovany : dnes)
    const zac = od < zaciatok ? zaciatok : od
    const kon = doDna > koniec ? koniec : doDna
    if (kon < zaciatok || zac > koniec) return null
    const i = rozdiel(zaciatok, zac)
    const sirka = Math.max(1, rozdiel(zac, kon) + 1)
    const poTermine = !vratene && planovany && planovany < dnes
    return {
      i, sirka, poTermine, vratene: !!vratene, planovany, od,
      farba: vratene ? '#94a3b8' : poTermine ? COLORS.remove : COLORS.brand,
      popis: v.polozka + ' — ' + (v.osoba || 'bez mena')
        + (v.project_id ? ' · ' + v.project_id + (v.zakazka_nazov ? ' ' + v.zakazka_nazov : '') : '')
        + '\nod ' + od.split('-').reverse().join('.')
        + (planovany ? ' · plán návratu ' + planovany.split('-').reverse().join('.') : '')
        + (vratene ? ' · vrátené ' + vratene.split('-').reverse().join('.') : ' · nevrátené (' + v.dni + ' dní)')
        + (poTermine ? '\nPO TERMÍNE' : ''),
    }
  }

  const sirkaDna = dni <= 14 ? 34 : dni <= 35 ? 22 : 13
  const tlacidlo = (on) => ({ padding: '4px 12px', fontSize: TYPO.sizes.xs, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
    borderRadius: RADIUS.md, color: on ? '#fff' : COLORS.nav, background: on ? COLORS.nav : COLORS.bg, border: `1px solid ${on ? COLORS.nav : COLORS.line}` })

  const poTermineSpolu = vypozicky.filter(v => !v.return_date && v.end_date && String(v.end_date).slice(0, 10) < dnes).length
  const vonkuSpolu = vypozicky.filter(v => !v.return_date).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm, minHeight: 0, flex: 1 }}>
      <style>{`@media print {
        body * { visibility: hidden; }
        #pozicovna-kalendar, #pozicovna-kalendar * { visibility: visible; }
        #pozicovna-kalendar { position: absolute; left: 0; top: 0; width: 100%; }
        #pozicovna-kalendar .bez-tlace { display: none !important; }
      }`}</style>

      <div className="bez-tlace" style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setZaciatok(plusDni(zaciatok, -7))} style={tlacidlo(false)} title="O týždeň späť">‹</button>
          <button onClick={() => setZaciatok(iso(pondelok(new Date(Date.now() - 7 * DEN_MS))))} style={tlacidlo(false)}>Dnes</button>
          <button onClick={() => setZaciatok(plusDni(zaciatok, 7))} style={tlacidlo(false)} title="O týždeň dopredu">›</button>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {ROZSAHY.map(r => <button key={r.k} onClick={() => setDni(r.k)} style={tlacidlo(dni === r.k)}>{r.t}</button>)}
        </div>
        <button onClick={() => setLenObsadene(v => !v)} style={tlacidlo(!lenObsadene)}>
          {lenObsadene ? 'Ukázať aj voľné stroje' : 'Len požičané'}
        </button>
        <input value={hladat} onChange={e => setHladat(e.target.value)} placeholder="Hľadať stroj, človeka, zákazku…"
          style={{ padding: '5px 10px', fontSize: TYPO.sizes.sm, fontFamily: 'inherit', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, minWidth: 190 }} />
        <button onClick={() => window.print()} style={tlacidlo(false)}>Tlačiť / PDF</button>
        <span style={{ marginLeft: 'auto', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          {zaciatok.split('-').reverse().join('.')} – {koniec.split('-').reverse().join('.')} ·
          {' '}vonku {vonkuSpolu}{poTermineSpolu ? ' · po termíne ' + poTermineSpolu : ''}
        </span>
      </div>

      <div id="pozicovna-kalendar" style={{ flex: '0 1 auto', maxHeight: '100%', overflow: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bg }}>
        <div style={{ display: 'grid', gridTemplateColumns: `220px repeat(${dni}, ${sirkaDna}px)`, minWidth: 220 + dni * sirkaDna }}>
          {/* hlavička */}
          <div style={{ position: 'sticky', top: 0, left: 0, zIndex: 3, background: '#eef5fa', borderBottom: '1px solid #d6e6f2', borderRight: `1px solid ${COLORS.line}`, padding: '6px 8px', fontSize: 11, fontWeight: TYPO.weights.bold, color: COLORS.nav, textTransform: 'uppercase' }}>
            Stroj
          </div>
          {stlpce.map(s => (
            <div key={s.d} title={s.d.split('-').reverse().join('.')}
              style={{ position: 'sticky', top: 0, zIndex: 2, background: s.dnes ? '#dbeafe' : s.vikend ? '#f1f5f9' : '#eef5fa', borderBottom: '1px solid #d6e6f2',
                borderLeft: s.cislo === 1 ? `2px solid ${COLORS.nav}` : '1px solid #e2e8f0', padding: '3px 0', textAlign: 'center', fontSize: 9, lineHeight: 1.15, color: s.dnes ? COLORS.nav : COLORS.muted, fontWeight: s.dnes ? 800 : 600 }}>
              {dni <= 35 ? <>{s.den}<br /></> : null}{s.cislo}
            </div>
          ))}
          {/* riadky */}
          {nacitava ? (
            <div style={{ gridColumn: `1 / span ${dni + 1}`, padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Načítavam…</div>
          ) : riadky.length === 0 ? (
            <div style={{ gridColumn: `1 / span ${dni + 1}`, padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
              V tomto období nie je nič požičané. Prepni na „Ukázať aj voľné stroje" alebo posuň obdobie.
            </div>
          ) : riadky.map(r => {
            const vonku = r.vyp.some(v => !v.return_date)
            return (
              <React.Fragment key={r.polozka.id}>
                <div onClick={() => onOtvorPolozku && onOtvorPolozku(r.polozka.id)}
                  title={r.polozka.name + (r.polozka.code ? ' · ' + r.polozka.code : '')}
                  style={{ position: 'sticky', left: 0, zIndex: 1, background: COLORS.bg, borderRight: `1px solid ${COLORS.line}`, borderBottom: `1px solid ${COLORS.lineSoft}`,
                    padding: '5px 8px', fontSize: TYPO.sizes.xs, cursor: onOtvorPolozku ? 'pointer' : 'default', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 6, background: vonku ? COLORS.brand : '#22c55e' }} />
                  <b>{r.polozka.name}</b>{r.polozka.code ? <span style={{ color: COLORS.muted }}> · {r.polozka.code}</span> : null}
                </div>
                <div style={{ gridColumn: `2 / span ${dni}`, position: 'relative', borderBottom: `1px solid ${COLORS.lineSoft}`, height: 26,
                  backgroundImage: `repeating-linear-gradient(to right, transparent 0 ${sirkaDna - 1}px, ${COLORS.lineSoft} ${sirkaDna - 1}px ${sirkaDna}px)` }}>
                  {stlpce.map((s, i) => s.vikend || s.dnes ? (
                    <div key={s.d} style={{ position: 'absolute', left: i * sirkaDna, width: sirkaDna, top: 0, bottom: 0, background: s.dnes ? 'rgba(59,130,246,0.12)' : 'rgba(148,163,184,0.10)' }} />
                  ) : null)}
                  {r.vyp.map(v => {
                    const p = pruh(v)
                    if (!p) return null
                    return (
                      <div key={v.id} title={p.popis} onClick={() => onOtvorPolozku && onOtvorPolozku(r.polozka.id)}
                        style={{ position: 'absolute', left: p.i * sirkaDna + 1, width: p.sirka * sirkaDna - 2, top: 3, height: 20, background: p.farba,
                          borderRadius: 4, color: '#fff', fontSize: 10, lineHeight: '20px', padding: '0 5px', overflow: 'hidden', whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis', cursor: 'pointer', opacity: p.vratene ? 0.65 : 1, boxSizing: 'border-box' }}>
                        {p.sirka * sirkaDna > 60 ? (v.osoba || 'bez mena') + (v.project_id && p.sirka * sirkaDna > 150 ? ' · ' + v.project_id : '') : ''}
                      </div>
                    )
                  })}
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>

      <div className="bez-tlace" style={{ display: 'flex', gap: SPACE.md, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: COLORS.brand }} /> požičané</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: COLORS.remove }} /> po plánovanom návrate</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#94a3b8' }} /> vrátené</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e' }} /> stroj je doma</span>
        <span>Klik na riadok alebo pruh otvorí stroj.</span>
      </div>
    </div>
  )
}
